import { useContext, useState, useEffect } from 'react';
import { useMutation, UseMutationResult, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import Button from 'design-system/src/components/Button/Button';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { Badge } from 'design-system/src/components/Badge/Badge';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { InfoBox } from 'design-system/src/components/InfoBox/InfoBox';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import { client, getRxFilePreviewUrl, TaskComment, TaskEvent } from '../../api';
import { TimelineItemList, TimelineItem } from '../TimelineItem/TimelineItem';
import { Sidebar, SidebarTopBar } from 'design-system/src/components/Sidebar/Sidebar';
import { DrugPanel, PatientPanel, PrescriberPanel } from '../sidebar/SidebarPanels';
import { TaskContext } from './contexts';
import { ItemViewTopNav } from '../tableItemDetailViewComponents/ItemViewTopNav';
import {
  BodyContainer,
  TopNavAndContent,
  ContentOuterWrapper,
  CenteredContent,
} from '../tableItemDetailViewComponents/Containers';
import { GeneralInfoTable } from 'design-system/src/components/GeneralInfoTable/GeneralInfoTable';
import LmnRespondedModal from './LmnRespondedModal/LmnRespondedModal';
import { FormActionContainer, StickyButtonContainer, StyledIframe } from './actions';
import BusinessRelationshipSmallPrint from '../businessRelationshipNotices/BusinessRelationshipSmallPrint/BusinessRelationshipSmallPrint';
import LetterOfMedicalNecessityEditModal from '../LmnEditModal';
import { FloatingEditButton } from '../FloatingEditButton/FloatingEditButton';
import type { LetterOfMedicalNecessityTask, WithAdditionalTaskResponseFields } from './types';

type BlockingLmnResponse = {
  blocking_lmn_id: string;
  lmn_id: string;
  document_id: string;
  editable_letter_content: string;
  status: string;
  type: string;
};

const FLOATING_BUTTON_TOP_OFFSET = '-90px';
const FLOATING_BUTTON_RIGHT_OFFSET = '32px';

interface InProgressViewProps {
  instructions: string;
  documentId: string;
  rxId: string;
  setResponseType: (responseType: ResponseType) => void;
  responseType: ResponseType;
  submit: UseMutationResult<void, Error, ResponseType, unknown>;
  blockingLmnId: string | undefined;
  onEditClick: () => void;
}
const InProgressView = ({
  instructions,
  documentId,
  rxId,
  setResponseType,
  responseType,
  submit,
  blockingLmnId,
  onEditClick,
}: InProgressViewProps) => {
  return (
    <>
      <Typography styledAs="bodySmallDMSans">
        <RenderHtmlString>{instructions}</RenderHtmlString>
      </Typography>

      <Hairline />

      <InfoBox includeIcon={false} variant="surface">
        <Typography weight={600}>
          Please review the letter and select an option below within 3 days to proceed.
        </Typography>
      </InfoBox>

      <FormActionContainer>
        <StyledIframe src={getRxFilePreviewUrl(rxId, documentId)} />

        <StickyButtonContainer>
          {blockingLmnId && (
            <FloatingEditButton
              top={FLOATING_BUTTON_TOP_OFFSET}
              right={FLOATING_BUTTON_RIGHT_OFFSET}
              onClick={onEditClick}
            />
          )}
          <ButtonRow align={Alignment.SpaceBetween}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setResponseType('decline');
                submit.mutate('decline');
              }}
              disabled={submit.isPending && responseType !== 'decline'}
              isLoading={submit.isPending && responseType === 'decline'}
            >
              Decline letter and submit PA
            </Button>

            <Button
              size="sm"
              onClick={() => {
                setResponseType('approve');
                submit.mutate('approve');
              }}
              disabled={submit.isPending && responseType !== 'approve'}
              isLoading={submit.isPending && responseType === 'approve'}
            >
              Approve letter and submit PA
            </Button>
          </ButtonRow>
        </StickyButtonContainer>
      </FormActionContainer>
    </>
  );
};

type ResponseType = 'approve' | 'decline';

/**
 * Version of TaskView that is used for LMN (Review Letter of Medical Necessity) tasks.
 */
const LetterOfMedicalNecessityTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<LetterOfMedicalNecessityTask>;
}> = ({ taskResponse }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showRespondedModal, setShowRespondedModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [responseType, setResponseType] = useState<ResponseType>('approve');
  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
  const [editableContent, setEditableContent] = useState<string>('');
  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;

  // Get blocking_lmn_id from task response
  const blockingLmnId = (task as any).blocking_lmn_id;

  // We're managing the document ID in the state so that it can be updated easily on edits in LmnEditModal
  const taskDocumentId = (task as any).document_id;
  useEffect(() => {
    if (taskDocumentId) {
      setCurrentDocumentId(taskDocumentId);
    }
  }, [taskDocumentId]);

  // Fetch blocking LMN data for editing (only needed to get editable_letter_content)
  const { data: blockingLmnData } = useQuery<BlockingLmnResponse>({
    queryKey: ['lmn-task', blockingLmnId],
    queryFn: async () => {
      const response = await client.get(`lmn-task/${blockingLmnId}`);
      return response.data;
    },
    enabled: !!blockingLmnId,
  });

  const submit = useMutation<void, Error, ResponseType>({
    mutationFn: async (responseType: ResponseType) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "submit LMN response" for task ID: ${taskId}`);
        return;
      }
      const formData = new FormData();
      formData.append('task_response', responseType === 'approve' ? 'approve_lmn' : 'decline_lmn');
      const res = await client.post(`tasks/${taskId}/respond`, formData);
      return res.data;
    },
    onSuccess: () => {
      onTaskUpdate();
      setShowRespondedModal(true);
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const handleEditClick = () => {
    if (blockingLmnData) {
      setEditableContent(blockingLmnData.editable_letter_content);
    }
    setShowEditModal(true);
  };

  let body;

  if (!task.timeline || !task.timeline.length) {
    Sentry.captureMessage('No initial comment found for LMN task', {
      level: 'error',
      extra: {
        taskId,
      },
    });
    body = <PageLoadError />;
  }

  const initialComment = task.timeline?.[0] as TaskComment | undefined;

  // Use document_id from task response (which comes from blocking_lmn.document_id in the backend)
  const documentId = currentDocumentId;

  let completedTimeline = null;

  if (task.status === 'completed') {
    if (task.timeline.length === 1 || !initialComment) {
      Sentry.captureMessage('No completion event found for LMN task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
      body = <PageLoadError />;
    } else {
      const completionEvent = task.timeline[1] as TaskEvent;
      completedTimeline = (
        <TimelineItemList>
          <TimelineItem
            author={initialComment.user}
            timestamp={initialComment.created_at}
            message={initialComment.message}
            attachedFilesIds={initialComment.attachment_document_ids}
            isLastEventInList={false}
          />
          <TimelineItem
            author={completionEvent.user}
            timestamp={completionEvent.event_timestamp}
            action={completionEvent.action}
            message={completionEvent.label}
            isLastEventInList={true}
          />
        </TimelineItemList>
      );
    }
  }

  const responseModal = (
    <LmnRespondedModal
      closeModal={() => setShowRespondedModal(false)}
      patient={patient}
      drug={drug}
      variant={responseType}
    />
  );

  body = body ?? (
    <BodyContainer>
      <TopNavAndContent>
        <ItemViewTopNav
          isSidebarCollapsed={isSidebarCollapsed}
          setIsSidebarCollapsed={setIsSidebarCollapsed}
          additionalRightSideContent={task.status === 'completed' && <Badge variant="neutral">Closed</Badge>}
        />

        <ContentOuterWrapper>
          <CenteredContent>
            <Typography styledAs="h6" renderedAs="h1" marginBottom="1rem">
              {task.type}
            </Typography>
            <GeneralInfoTable
              patientName={drug.patient_name}
              prescriberName={drug.prescriber_name}
              dob={drug.dob}
              prescribedOn={drug.prescription_recieved_at}
              drugName={drug.name}
              prescriptionId={drug.id}
            />

            {task.status !== 'completed'
              ? initialComment && (
                  <InProgressView
                    instructions={initialComment.message}
                    documentId={documentId}
                    rxId={drug.id}
                    setResponseType={setResponseType}
                    responseType={responseType}
                    submit={submit}
                    blockingLmnId={blockingLmnId}
                    onEditClick={handleEditClick}
                  />
                )
              : completedTimeline}

            <BusinessRelationshipSmallPrint />
          </CenteredContent>
        </ContentOuterWrapper>
      </TopNavAndContent>

      {!isSidebarCollapsed && (
        <Sidebar>
          <SidebarTopBar />
          <DrugPanel drug={drug} showBusinessRelationshipNotice />
          <PatientPanel patient={patient} />
          <PrescriberPanel prescriber={prescriber} />
        </Sidebar>
      )}
    </BodyContainer>
  );

  return (
    <>
      {body}

      {showRespondedModal && responseType && responseModal}

      {showEditModal && blockingLmnId && blockingLmnData && (
        <LetterOfMedicalNecessityEditModal
          context="blocking_lmn"
          blockingLmnId={blockingLmnId}
          lmnId={blockingLmnData.lmn_id}
          letterContent={editableContent}
          closeModal={() => setShowEditModal(false)}
          setDocumentId={setCurrentDocumentId}
          setEditableContent={setEditableContent}
        />
      )}
    </>
  );
};

export default LetterOfMedicalNecessityTaskView;
